import { authLog, authTrail, fingerprint } from 'src/sentry';

// The native shell (React Native WebView) injects this bridge; it is absent
// in plain browsers. Declared globally so every call site sees the same shape.
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

/** Messages exchanged with the native app: a `type` tag plus a free-form payload. */
export type AppMessage = { type?: string; token?: string } & Record<string, unknown>;

export const postToApp = (type: string, data: Record<string, unknown> = {}) => {
  if (window.ReactNativeWebView) {
    // log type plus token fingerprint/null, never the raw token
    authTrail('postToApp', { type, ...('token' in data ? { tokenFp: fingerprint(data.token) } : {}) });
    window.ReactNativeWebView.postMessage(JSON.stringify({ type, ...data }));
  }
};

const REQUEST_FROM_APP_TIMEOUT_MS = 5000;

let requestSeq = 0;

export const requestFromApp = (
  type: string,
  { timeout = REQUEST_FROM_APP_TIMEOUT_MS }: { timeout?: number } = {}
): Promise<AppMessage> => {
  if (!window.ReactNativeWebView) return Promise.resolve({});
  requestSeq += 1;
  const reqId = requestSeq;
  const startedAt = Date.now();
  return new Promise((resolve) => {
    // request id/type/start so native response latency and duplicate requests are visible
    authTrail('requestFromApp:start', { reqId, type });
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage as EventListener);
    };
    const handleMessage = (event: MessageEvent) => {
      try {
        const data: AppMessage = JSON.parse(event.data);
        if (data.type === type) {
          // request id/type/duration and whether token was present; never the raw token
          authTrail('requestFromApp:response', {
            reqId,
            type,
            durationMs: Date.now() - startedAt,
            hasToken: !!data.token,
            tokenFp: fingerprint(data.token),
          });
          cleanup();
          resolve(data);
        }
      } catch {
        // Non-JSON message, ignore.
      }
    };
    const timer = setTimeout(() => {
      // timeout — auth timeouts can cascade into token clears after 401s, so surface as a searchable event
      authLog('requestFromApp:timeout', { reqId, type, durationMs: Date.now() - startedAt });
      cleanup();
      resolve({});
    }, timeout);
    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage as EventListener);
    postToApp(type);
  });
};
